/**
 * User Preferences Store
 * Preferences are scoped per user, with context-specific settings per retailer/environment.
 * - Theme: Global user preference
 * - Other settings: Per retailer/environment context
 * - DLQ & telemetry time ranges: Per retailer/environment context
 * @packageDocumentation
 */

import { create } from 'zustand';
import type { StateStorage } from 'zustand/middleware';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { TimeRange } from '@/hooks/useMonitoring';

/**
 * Gets the current user ID from the auth store in localStorage.
 * This allows preferences to be stored per user.
 *
 * @returns { string | null } The current user ID or null if not authenticated.
 */
const getCurrentUserId = (): string | null => {
  try {
    const authState = localStorage.getItem('nexus-admin-auth');

    if (!authState) {
      return null;
    }

    const parsed = JSON.parse(authState);

    return parsed?.state?.user?.id ?? null;
  } catch {
    return null;
  }
};

/**
 * Custom storage implementation that creates per-user preference keys.
 *
 * @returns { StateStorage } The custom storage implementation.
 */
const createUserScopedStorage = (): StateStorage => ({
  removeItem: (name: string): void => {
    const userId = getCurrentUserId();
    const key = userId ? `${name}:${userId}` : name;

    localStorage.removeItem(key);
  },
  getItem: (name: string): string | null => {
    const userId = getCurrentUserId();
    const key = userId ? `${name}:${userId}` : name;

    return localStorage.getItem(key);
  },
  setItem: (name: string, value: string): void => {
    const userId = getCurrentUserId();
    const key = userId ? `${name}:${userId}` : name;

    localStorage.setItem(key, value);
  },
});

/**
 * Supported application theme options.
 * Values are persisted to localStorage and used as the <html> class name.
 */
export enum Theme {
  Light = 'light',
  Dark = 'dark',
  System = 'system',
}
/**
 * Union type for supported date format options.
 */
export type DateFormat = 'iso' | 'us' | 'eu' | 'relative';

/**
 * Canonical, ordered list of DLQ time range filter options.
 * Single source of truth — both the union type and the DLQ page dropdown derive from this.
 */
export const DLQ_TIME_RANGES = ['last 24 hours', 'last 7 days', 'last 30 days', 'last 90 days', 'All time'] as const;

/**
 * Union type for DLQ time range filter options.
 */
export type DlqTimeRange = (typeof DLQ_TIME_RANGES)[number];

/**
 * Context-specific preferences for a retailer/environment combination.
 */
interface ContextPreferences {
  compactMode: boolean;
  dateFormat: DateFormat;
  dlqTimeRanges: Record<string, DlqTimeRange>;
  telemetryTimeRanges: Record<string, TimeRange>;
}

/**
 * Interface representing the user preferences store state and actions.
 */
export interface PreferencesState {
  resetPreferences: () => void;
  contexts: Record<string, ContextPreferences>;
  getCompactMode: (retailerCode: string, environment: string) => boolean;
  getDateFormat: (retailerCode: string, environment: string) => DateFormat;
  setCompactMode: (retailerCode: string, environment: string, compact: boolean) => void;
  setDateFormat: (retailerCode: string, environment: string, format: DateFormat) => void;
  getDlqTimeRange: (retailerCode: string, environment: string, dlqType: string) => DlqTimeRange;
  getTelemetryTimeRange: (retailerCode: string, environment: string, chartId: string) => TimeRange;
  setDlqTimeRange: (retailerCode: string, environment: string, dlqType: string, range: DlqTimeRange) => void;
  setTelemetryTimeRange: (retailerCode: string, environment: string, chartId: string, range: TimeRange) => void;
}

const DEFAULT_CONTEXT_PREFERENCES: ContextPreferences = Object.freeze({
  dlqTimeRanges: {},
  dateFormat: 'iso',
  compactMode: false,
  telemetryTimeRanges: {},
});

const DEFAULT_DLQ_TIME_RANGE: DlqTimeRange = 'last 24 hours';

/** Valid DLQ time range values, used to migrate stale persisted preferences from older option sets. */
const VALID_DLQ_TIME_RANGES = new Set<string>(DLQ_TIME_RANGES);

const DEFAULT_TELEMETRY_TIME_RANGE: TimeRange = '24h';

/**
 * Creates a context key for retailer/environment combination.
 * Retailer codes are normalized to lowercase for consistent storage.
 *
 * @param { string } retailerCode The retailer code.
 * @param { string } environment The environment name.
 * @returns { string } The context key.
 */
const createContextKey = (retailerCode: string, environment: string): string =>
  `${retailerCode.toLowerCase()}:${environment}`;

/**
 * Gets or creates context preferences for a retailer/environment.
 *
 * @param { Record<string, ContextPreferences> } contexts All contexts.
 * @param { string } contextKey The context key.
 * @returns { ContextPreferences } The context preferences.
 */
const getOrCreateContext = (contexts: Record<string, ContextPreferences>, contextKey: string): ContextPreferences => {
  const stored = contexts[contextKey];

  if (!stored) {
    return DEFAULT_CONTEXT_PREFERENCES;
  }

  // Merge defaults for fields that may be missing from older persisted data
  return { ...DEFAULT_CONTEXT_PREFERENCES, ...stored };
};

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set, get) => ({
      contexts: {},
      resetPreferences: () =>
        set({
          contexts: {},
        }),
      getDateFormat: (retailerCode, environment) => {
        const contextKey = createContextKey(retailerCode, environment);
        const context = getOrCreateContext(get().contexts, contextKey);

        return context.dateFormat;
      },
      getCompactMode: (retailerCode, environment) => {
        const contextKey = createContextKey(retailerCode, environment);
        const context = getOrCreateContext(get().contexts, contextKey);

        return context.compactMode;
      },
      getTelemetryTimeRange: (retailerCode, environment, chartId) => {
        const contextKey = createContextKey(retailerCode, environment);
        const context = getOrCreateContext(get().contexts, contextKey);

        return context.telemetryTimeRanges[chartId] ?? DEFAULT_TELEMETRY_TIME_RANGE;
      },
      setDateFormat: (retailerCode, environment, dateFormat) => {
        const contextKey = createContextKey(retailerCode, environment);

        set((state) => ({
          contexts: {
            ...state.contexts,
            [contextKey]: {
              ...getOrCreateContext(state.contexts, contextKey),
              dateFormat,
            },
          },
        }));
      },
      setCompactMode: (retailerCode, environment, compactMode) => {
        const contextKey = createContextKey(retailerCode, environment);

        set((state) => ({
          contexts: {
            ...state.contexts,
            [contextKey]: {
              ...getOrCreateContext(state.contexts, contextKey),
              compactMode,
            },
          },
        }));
      },
      getDlqTimeRange: (retailerCode, environment, dlqType) => {
        const contextKey = createContextKey(retailerCode, environment);
        const context = getOrCreateContext(get().contexts, contextKey);
        const stored = context.dlqTimeRanges[dlqType];

        // Migrate stale/removed persisted values (e.g. the old hour-based options) to the default
        return stored !== undefined && VALID_DLQ_TIME_RANGES.has(stored) ? stored : DEFAULT_DLQ_TIME_RANGE;
      },
      setDlqTimeRange: (retailerCode, environment, dlqType, range) => {
        const contextKey = createContextKey(retailerCode, environment);

        set((state) => ({
          contexts: {
            ...state.contexts,
            [contextKey]: {
              ...getOrCreateContext(state.contexts, contextKey),
              dlqTimeRanges: {
                ...getOrCreateContext(state.contexts, contextKey).dlqTimeRanges,
                [dlqType]: range,
              },
            },
          },
        }));
      },
      setTelemetryTimeRange: (retailerCode, environment, chartId, range) => {
        const contextKey = createContextKey(retailerCode, environment);

        set((state) => {
          const context = getOrCreateContext(state.contexts, contextKey);

          return {
            contexts: {
              ...state.contexts,
              [contextKey]: {
                ...context,
                telemetryTimeRanges: {
                  ...context.telemetryTimeRanges,
                  [chartId]: range,
                },
              },
            },
          };
        });
      },
    }),
    {
      name: 'nexus-admin-preferences',
      storage: createJSONStorage(() => createUserScopedStorage()),
      partialize: (state) => ({
        contexts: state.contexts,
      }),
    },
  ),
);

/**
 * Formats a date according to the specified format preference.
 *
 * @param { Date | string } date The date to format.
 * @param { DateFormat } format The format to use for display.
 * @returns { string } The formatted date string.
 */
export const formatDate = (date: Date | string, format: DateFormat): string => {
  const d = typeof date === 'string' ? new Date(date) : date;

  if (Number.isNaN(d.getTime())) {
    return 'Invalid date';
  }

  switch (format) {
    case 'iso':
      return d.toISOString().split('T')[0] ?? '';

    case 'us':
      return d.toLocaleDateString('en-US', {
        day: '2-digit',
        year: 'numeric',
        month: '2-digit',
      });

    case 'eu':
      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        year: 'numeric',
        month: '2-digit',
      });

    case 'relative':
      return formatRelativeDate(d);

    default:
      return d.toISOString().split('T')[0] ?? '';
  }
};

const formatRelativeDate = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) {
    return 'just now';
  }

  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }

  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }

  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  });
};

/**
 * Formats a date and time according to the specified format preference.
 *
 * @param { Date | string } date The date to format.
 * @param { DateFormat } format The format to use for display.
 * @returns { string } The formatted date-time string.
 */
export const formatDateTime = (date: Date | string, format: DateFormat): string => {
  const d = typeof date === 'string' ? new Date(date) : date;

  if (Number.isNaN(d.getTime())) {
    return 'Invalid date';
  }

  const dateStr = formatDate(d, format);
  const timeStr = d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: format === 'us',
  });

  return `${dateStr} ${timeStr}`;
};
